import React from 'react';
import { LoaderSpinner } from '../loader-spinner';
import { getStringifiedServiceConfigsServiceConfig } from '../../services/get-stringified-service-configs-service-config';
import { useFeatureAppConfig } from '../../services/use-feature-app-config';
import { ShowroomModel } from '../../typings/model-overview';
import { useFeatureServices } from '../../services/use-feature-services';
import { fetchInitialReviewsDataCSR } from '../../fetch-data';
import { ReviewModel } from '../../typings/reviews';
import _ from 'lodash';
import { ReviewsDataService } from '../../services/reviews-data-service';
import ReviewsPreview from '../../page/reviews-preview';
import AllReviews from '../../page/all-reviews';
import { ErrorHandlingModel } from '../../hooks-store/typings/store';
import { ErrorMessageBox } from '../error-message';
import { useTrackingManager } from '../../services/use-tracking-manager';
import {
  onHandleBasicVehicleLink,
  onHandleLayerCloseTrackEvent,
  onHandleLayerLoadTrackEvent,
} from '../../utils/tracking-helpers';
import ModelReviews from '../../page/model-reviews';
import { Spinner } from '@vw-marketing/us-components';

interface HandleDataLoadProps {
  readonly baseUrl?: string;
  readonly showRoomModel?: ShowroomModel;
}

export const HandleDataLoad: React.FC<HandleDataLoadProps> = (
  props,
): JSX.Element | null => {
  const { showRoomModel } = props;
  const {
    'locale-service': localeService,
    'service-config-provider': serviceProvider,
    's2:server-request': serverRequest,
  } = useFeatureServices();
  const { apiKeyContent, mode } = useFeatureAppConfig();
  const serviceConfigsServiceConfig = getStringifiedServiceConfigsServiceConfig(
    serviceProvider,
    serverRequest,
  );
  const reviewsHostData = serviceProvider.configs['fiveStars'];
  const {
    mockIds,
    mocksBaseUrl,
    faServicesUrl,
    imageModel,
    modelConfiguration,
  } = useFeatureAppConfig();
  const [reviews, setReviews] = React.useState<ReviewModel | undefined>(
    undefined,
  );
  const [showAllReviews, setShowAllReviews] = React.useState<boolean>(false);
  const [errorState, setErrorState] = React.useState<boolean>(false);
  const isModelReview = mode === "model-reviews";
  const trackingManager = useTrackingManager();
  const getInitialData = () => {
    reviews && setReviews(undefined);
    fetchInitialReviewsDataCSR({
      faServicesUrl,
      reviewsHostData,
      localeService,
      serviceConfigsServiceConfig,
      showRoomModel,
      mockIds,
      mocksBaseUrl,
      imageModel,
      modelConfiguration,
      apiKeyContent,
    })
      .then((reviewsResponse: ReviewModel) => {
        if (reviewsResponse && _.isNumber(reviewsResponse.averageRating)) {
          setReviews(reviewsResponse);
          // Tracking section
          onHandleLayerLoadTrackEvent('load', trackingManager, reviewsResponse);
        } else {
          setErrorState(true);
        }
      })
      .catch((error: ErrorHandlingModel[]) => {
        if (_.isArray(error) && error.length) {
          setErrorState(true);
        }
      });
  };

  React.useEffect(() => {
    getInitialData();
  }, []);

  const onCloseHandler = () => {
    setShowAllReviews(false);
    onHandleLayerCloseTrackEvent(trackingManager, reviews);
  };

  if (errorState && !isModelReview) {
    return (
      <>
         <ErrorMessageBox label={'There is a problem getting reviews data'} />
      </>
    );
  }

  const onHandleAllReviewsPage = (display: boolean) => {
    setShowAllReviews(display);
    onHandleBasicVehicleLink(
      'Open reviews modal',
      'View all',
      trackingManager,
      reviews,
    );
    onHandleLayerLoadTrackEvent('layerLoad', trackingManager, reviews);
  };
  return (
    <>
      {reviews ? (
        <ReviewsDataService reviews={reviews}>
          {' '}
          {isModelReview ? (
            <ModelReviews
              reviews={reviews}
              onOpenReviews={onHandleAllReviewsPage}
            />
          ) : (
            <ReviewsPreview
              reviews={reviews}
              handleOnViewAll={onHandleAllReviewsPage}
              isModalActive={showAllReviews}
              setReviews={setReviews}
            />
          )}
          <AllReviews
            active={showAllReviews}
            onCloseHandler={onCloseHandler}
            reviews={reviews}
            trackingManager={trackingManager}
          />
        </ReviewsDataService>
      ) : (
        <>
        {isModelReview ? <Spinner/>:<LoaderSpinner label={'Loading...'} />}
          
        </>
      )}
    </>
  );
};
